import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
    Plus,
    Folder,
    Share2,
    Link2,
    Trash2,
    Pencil,
    Copy,
    EyeOff,
    Check,
    X,
} from "lucide-react";
import { api_endpoints } from "@/lib/api";
import {
    PageHeader,
    Btn,
    Card,
    Eyebrow,
    EmptyState,
    Field,
    TextInput,
} from "@/components/app/ui";

export default function Collections() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [creating, setCreating] = React.useState(false);
    const [newName, setNewName] = React.useState("");
    const [renameId, setRenameId] = React.useState(null);
    const [renameDraft, setRenameDraft] = React.useState("");
    const newCollectionButtonRef = React.useRef(null);
    const createTriggerRef = React.useRef(null);
    const renameButtonRefs = React.useRef(new Map());
    const renameFocusIdRef = React.useRef(null);

    function returnFocus(target) {
        requestAnimationFrame(() => target?.focus());
    }

    React.useEffect(() => {
        if (renameId === null && !loading && renameFocusIdRef.current) {
            const id = renameFocusIdRef.current;
            const target = renameButtonRefs.current.get(id);
            if (target?.isConnected) {
                renameFocusIdRef.current = null;
                returnFocus(target);
            }
        }
    }, [items, loading, renameId]);

    function openCreate(event) {
        createTriggerRef.current = event?.currentTarget || newCollectionButtonRef.current;
        setCreating(true);
    }

    function restoreCreateFocus() {
        const original = createTriggerRef.current;
        createTriggerRef.current = null;
        const target = original?.isConnected ? original : newCollectionButtonRef.current;
        returnFocus(target);
    }

    function cancelCreate() {
        setCreating(false);
        setNewName("");
        restoreCreateFocus();
    }

    function closeRename(id) {
        renameFocusIdRef.current = id;
        setRenameId(null);
    }

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const r = await api_endpoints.collections();
            setItems(r || []);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    async function create() {
        if (!newName.trim()) return;
        try {
            await api_endpoints.createCollection(newName.trim());
            toast.success(t("collections.collectionCreated"));
            setNewName("");
            setCreating(false);
            await load();
            restoreCreateFocus();
        } catch (e) {
            toast.error(e.message);
        }
    }

    async function rename(c) {
        if (!renameDraft.trim()) return;
        try {
            await api_endpoints.renameCollection(c.id, renameDraft.trim());
            toast.success(t("collections.renamed"));
            closeRename(c.id);
            await load();
        } catch (e) {
            toast.error(e.message);
        }
    }

    async function remove(c) {
        if (!window.confirm(t("collections.deleteConfirm", { name: c.name })))
            return;
        try {
            await api_endpoints.deleteCollection(c.id);
            toast.success(t("collections.collectionDeleted"));
            load();
        } catch (e) {
            toast.error(e.message);
        }
    }

    async function share(c) {
        try {
            const r = await api_endpoints.shareCollection(c.id);
            const url = `${window.location.origin}/shared/${r.token}`;
            navigator.clipboard.writeText(url);
            toast.success(t("collections.shareLinkCopied"));
            load();
        } catch (e) {
            toast.error(e.message);
        }
    }

    async function revoke(c) {
        if (!window.confirm(t("collections.revokeConfirm")))
            return;
        try {
            await api_endpoints.revokeShare(c.id);
            toast.success(t("collections.shareRevoked"));
            load();
        } catch (e) {
            toast.error(e.message);
        }
    }

    function copyShareLink(c) {
        const url = `${window.location.origin}/shared/${c.shareToken}`;
        navigator.clipboard.writeText(url);
        toast.success(t("collections.linkCopied"));
    }

    return (
        <div>
            <PageHeader
                eyebrow={t("collections.eyebrow", { count: items.length })}
                title={t("collections.title")}
                actions={
                    <Btn
                        variant="primary"
                        icon={Plus}
                        onClick={openCreate}
                        testid="new-collection-btn"
                        ref={newCollectionButtonRef}
                    >
                        {t("collections.newCollection")}
                    </Btn>
                }
            />

            <div className="p-6 md:p-10 space-y-6">
                {creating && (
                    <Card
                        className="p-5"
                        testid="new-collection-card"
                        role="dialog"
                        aria-label={t("collections.newCollection")}
                    >
                        <Eyebrow>── {t("collections.newCollection")}</Eyebrow>
                        <div className="mt-3 flex flex-wrap items-end gap-3">
                            <Field label={t("collections.newCollectionLabel")} testid="new-name">
                                <TextInput
                                    value={newName}
                                    onChange={setNewName}
                                    placeholder={t("collections.newCollectionPlaceholder")}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") create();
                                        if (e.key === "Escape") cancelCreate();
                                    }}
                                    testid="new-collection-name"
                                />
                            </Field>
                            <Btn variant="primary" icon={Check} onClick={create} testid="create-collection-submit">
                                {t("collections.create")}
                            </Btn>
                            <Btn
                                variant="outline"
                                icon={X}
                                onClick={cancelCreate}
                            >
                                {t("collections.cancel")}
                            </Btn>
                        </div>
                    </Card>
                )}

                {loading ? (
                    <Card className="p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                        {t("collections.loading")}
                    </Card>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={Folder}
                        title={t("collections.emptyTitle")}
                        description={t("collections.emptyDescription")}
                        action={
                            <Btn variant="primary" icon={Plus} onClick={openCreate}>
                                {t("collections.createCollection")}
                            </Btn>
                        }
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
                        {items.map((c) => (
                            <Card
                                key={c.id}
                                className="p-5 bg-[#FFCC00]/5 hover:bg-[#FFCC00]/15 transition-colors border-2 border-black cursor-pointer"
                                testid={`collection-${c.id}`}
                                onClick={() => {
                                    localStorage.setItem("spotcar-dashboard-collection", c.id);
                                    navigate("/app/dashboard");
                                }}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        {renameId === c.id ? (
                                            <div onClick={(e) => e.stopPropagation()}>
                                            <TextInput
                                                value={renameDraft}
                                                onChange={setRenameDraft}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") rename(c);
                                                    if (e.key === "Escape") closeRename(c.id);
                                                }}
                                                testid={`rename-input-${c.id}`}
                                            />
                                            </div>
                                        ) : (
                                            <h3 className="font-display text-lg uppercase tracking-tight leading-tight truncate">
                                                {c.name}
                                            </h3>
                                        )}
                                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
                                            {t("collections.cars", { count: c.itemCount })}
                                            {c.isDefault && ` · ${t("collections.default")}`}
                                        </div>
                                    </div>
                                    <Folder size={20} className="text-zinc-400 flex-shrink-0" />
                                </div>

                                {c.isShared && (
                                    <div data-sensitive className="mt-4 border-2 border-black bg-[#FFCC00] p-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        <Link2 size={12} />
                                        <span className="font-mono text-[10px] truncate flex-1">
                                            /shared/{c.shareToken?.slice(0, 16)}…
                                        </span>
                                        <button
                                            onClick={() => copyShareLink(c)}
                                            data-testid={`copy-share-${c.id}`}
                                            title={t("collections.copyLink")}
                                            className="border border-black p-2 rounded bg-white text-black hover:bg-black hover:text-white"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                )}

                                <div className="mt-4 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    <Btn
                                        size="sm"
                                        onClick={() => {
                                            localStorage.setItem("spotcar-dashboard-collection", c.id);
                                            navigate("/app/dashboard");
                                        }}
                                        variant="outline"
                                        testid={`view-${c.id}`}
                                    >
                                        {t("collections.viewCars")}
                                    </Btn>
                                    {!c.isShared ? (
                                        <Btn
                                            size="sm"
                                            variant="primary"
                                            icon={Share2}
                                            onClick={() => share(c)}
                                            testid={`share-${c.id}`}
                                        >
                                            {t("collections.share")}
                                        </Btn>
                                    ) : (
                                        <Btn
                                            size="sm"
                                            variant="outline"
                                            icon={EyeOff}
                                            onClick={() => revoke(c)}
                                            testid={`revoke-${c.id}`}
                                        >
                                            {t("collections.revoke")}
                                        </Btn>
                                    )}
                                    {!c.isDefault && renameId !== c.id && (
                                        <>
                                            <Btn
                                                size="sm"
                                                variant="ghost"
                                                icon={Pencil}
                                                onClick={() => {
                                                    setRenameId(c.id);
                                                    setRenameDraft(c.name);
                                                }}
                                                testid={`rename-${c.id}`}
                                                ref={(node) => {
                                                    if (node) renameButtonRefs.current.set(c.id, node);
                                                    else renameButtonRefs.current.delete(c.id);
                                                }}
                                            >
                                                {t("collections.rename")}
                                            </Btn>
                                            <Btn
                                                size="sm"
                                                variant="ghost"
                                                icon={Trash2}
                                                onClick={() => remove(c)}
                                                testid={`delete-${c.id}`}
                                            >
                                                {t("collections.delete")}
                                            </Btn>
                                        </>
                                    )}
                                    {renameId === c.id && (
                                        <>
                                            <Btn
                                                size="sm"
                                                variant="primary"
                                                icon={Check}
                                                onClick={() => rename(c)}
                                            >
                                                {t("collections.save")}
                                            </Btn>
                                            <Btn
                                                size="sm"
                                                variant="outline"
                                                icon={X}
                                                onClick={() => closeRename(c.id)}
                                            >
                                                {t("collections.cancel")}
                                            </Btn>
                                        </>
                                    )}
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
